/**
 * features/exams/useExamDuties.ts — the rota, and what it would cost.
 *
 * Three reads, once for the screen: the duties, the school timetable (so a
 * duty can be checked against the lesson it would empty) and the bell times
 * (without them a window like 08:00–09:00 cannot be turned into periods).
 *
 * The timetable is ~1,150 documents and is deliberately NOT in DataContext,
 * for the reason recorded in useTimetableData: it matters on a handful of
 * screens. It is fetched here, once, by the screens that need it — and read
 * ONCE rather than subscribed, because a rota is worked on by one person at a
 * desk, not watched.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useTeachingCycle } from '@/lib/useTeachingCycle';
import {
  classesByTeacher, slotTeacherIds, teacherPhases,
  type ScheduleSettings, type TimetableSlot, type Day,
} from '@/lib/timetable';
import { phaseDivisionsOfTeacher } from '@/lib/divisions';
import {
  phaseOfPaper, subjectsMatch,
  type ExamPaperDuties, type PeriodTimes, type SchoolPhase,
} from '@/lib/examDuties';
import type { ExamSeries } from '@/lib/examTimetable';

/** Exams are secondary; the primary bell schedule is a different document. */
const DIVISION = 'secondary';

export interface DutyData {
  papers: ExamPaperDuties[];
  slots: TimetableSlot[];
  schedule: ScheduleSettings | null;
  /** Does this teacher teach the subject this paper is in? */
  teachesPaper: (teacherId: string, paper: ExamPaperDuties) => boolean;
  /** Which part of the school sits this paper — null when it is not known. */
  phaseOf: (paper: ExamPaperDuties) => SchoolPhase | null;
  /** The part(s) of the school a teacher works in — empty when not known. */
  phasesOfTeacher: (teacherId: string) => ReadonlySet<SchoolPhase>;
  /** The school's own exams (rule 5 in lib/examDuties): no FCS, a sitting
   *  form's lessons do not run, and HODs do not invigilate. */
  internal: boolean;
  loading: boolean;
  error: string;
}

const NO_PHASE: ReadonlySet<SchoolPhase> = new Set();

export function useExamDuties(seriesId: string): DutyData {
  // LKC: an exam date's lessons are its CYCLE day's (lib/examDuties dayOf).
  useTeachingCycle();
  const { teachers, subjects } = useData();
  const [papers, setPapers] = useState<ExamPaperDuties[]>([]);
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [schedule, setSchedule] = useState<ScheduleSettings | null>(null);
  /* The series itself, for ONE fact: which classes sit it. That is what makes
     "is this a primary or a secondary exam" a thing the screen reads rather
     than assumes — see `phaseOfPaper`. One document. */
  const [series, setSeries] = useState<ExamSeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      try {
        const [dutySnap, slotSnap, scheduleSnap, seriesSnap] = await Promise.all([
          getDocs(query(collection(db, 'reportExamDuties'), where('seriesId', '==', seriesId))),
          getDocs(collection(db, 'timetableSlots')),
          getDoc(doc(db, 'timetableSchedules', DIVISION)),
          getDoc(doc(db, 'reportExamSeries', seriesId)),
        ]);
        if (!live) return;
        setSeries(seriesSnap.exists() ? (seriesSnap.data() as ExamSeries) : null);
        setPapers(dutySnap.docs.map((d) => ({ ...(d.data() as ExamPaperDuties), id: d.id })));
        setSlots(slotSnap.docs.map((d) => ({ ...(d.data() as TimetableSlot), id: d.id })));
        setSchedule(scheduleSnap.exists() ? (scheduleSnap.data() as ScheduleSettings) : null);
        setError('');
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'Could not load the rota.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [seriesId]);

  /* What each teacher teaches, from BOTH records the school keeps: the HR
     assignment (subject ids) and the timetable itself (subject labels). A
     teacher covering a set they were never assigned is on the timetable only,
     and one whose subject is not timetabled this year is in HR only. */
  const subjectsByTeacher = useMemo(() => {
    const nameOf = new Map((subjects ?? []).map((s) => [s.id, s.name]));
    const by = new Map<string, Set<string>>();
    const add = (teacherId: string, label?: string | null) => {
      if (!teacherId || !label) return;
      const set = by.get(teacherId) ?? new Set<string>();
      set.add(label);
      by.set(teacherId, set);
    };
    for (const t of teachers ?? []) {
      for (const ids of Object.values(t.teachingAssignments ?? {})) {
        for (const id of ids ?? []) add(t.id, nameOf.get(id) ?? id);
      }
      for (const id of Object.keys(t.classAssignments ?? {})) add(t.id, nameOf.get(id) ?? id);
    }
    for (const slot of slots) {
      for (const teacherId of slotTeacherIds(slot)) add(teacherId, slot.subjectLabel);
      for (const g of slot.groups ?? []) {
        for (const teacherId of g.teacherIds ?? []) add(teacherId, g.subjectLabel);
      }
    }
    return by;
  }, [teachers, subjects, slots]);

  const teachesPaper = useMemo(() => (teacherId: string, paper: ExamPaperDuties) => {
    const mine = subjectsByTeacher.get(teacherId);
    if (!mine) return false;
    for (const label of mine) if (subjectsMatch(label, paper.subject)) return true;
    return false;
  }, [subjectsByTeacher]);

  /* Which classes sit this series, as the office recorded them on the set-up
     screen. Not per paper — the rota screens deliberately do not load the 113
     statements of entry — but a series whose classes are all one phase gives
     every one of its papers that phase, and a mixed one falls back to the
     paper's own code. */
  const sittingClasses = useMemo(
    () => (series?.classes ?? []).map((c) => c.className).filter(Boolean),
    [series],
  );

  const phaseOf = useMemo(
    () => (paper: ExamPaperDuties) => phaseOfPaper(paper, sittingClasses),
    [sittingClasses],
  );

  /* A teacher's phase, by the school-wide rule in lib/timetable: the UNION of
     what HR recorded and what they are actually timetabled to teach. Primary
     is not timetabled yet, so primary staff rest on HR's `divisions` alone —
     live, all 74 teachers carry one. */
  const phasesById = useMemo(() => {
    const taught = classesByTeacher(slots);
    const out = new Map<string, ReadonlySet<SchoolPhase>>();
    for (const t of teachers ?? []) {
      out.set(t.id, teacherPhases(phaseDivisionsOfTeacher(t), taught.get(t.id) ?? []));
    }
    return out;
  }, [teachers, slots]);

  const phasesOfTeacher = useMemo(
    () => (teacherId: string) => phasesById.get(teacherId) ?? NO_PHASE,
    [phasesById],
  );

  return { papers, slots, schedule, teachesPaper, phaseOf, phasesOfTeacher, internal: series?.kind === 'internal', loading, error };
}

/** The teaching periods of one weekday, as clock times. */
export function periodTimesFor(schedule: ScheduleSettings | null, day: Day | null): PeriodTimes[] {
  if (!schedule || !day) return [];
  return (schedule.days?.[day] ?? []).flatMap((slot) => {
    if (typeof slot.period !== 'number') return [];
    const [from, to] = String(slot.time ?? '').split('-').map((s) => s.trim());
    return from && to ? [{ period: slot.period, from, to }] : [];
  });
}
